// @flow
import EditIcon from "@mui/icons-material/Edit";
import LoadingButton from "@mui/lab/LoadingButton";
import { Paper } from "@mui/material";
import Box from "@mui/material/Box";
import {
	DataGridPremium, GridActionsCellItem,
	gridFilterModelSelector,
	gridSortModelSelector, gridVisibleColumnDefinitionsSelector, useGridApiRef
} from "@mui/x-data-grid-premium";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import TrashIcon from "../../../../../../assets/images/icons/buttons/trashIcon";
import ConfirmDialog from "../../../../../../components/app/dialogs/confirmDialog/confirmDialog";
import TruncateDescription from "../../../../../../components/app/truncateDescription";
import CustomTranslation from "../../../../../../components/general/customTranslation/customTranslation";
import {
	CustomPagination
} from "../../../../../../components/grids/dataGridCustom/pagination/customPaginations/customPagination";
import CustomToolbar from "../../../../../../components/grids/dataGridCustom/toolbars/customToolbar/customToolbar";
import DataGridWrapper from "../../../../../../components/grids/dataGridWrapper/dataGridWrapper";
import { API } from "../../../../../../helpers/constants";
import { handleAxiosErrors } from "../../../../../../helpers/errorHandling";
import { downloadFile } from "../../../../../../helpers/fileHandle";
import { muiGridLocales } from "../../../../../../helpers/muiGridLocales";
import { useExhibition } from "../../../../../../hooks/exhibitions/useExhibition";
import { useMuiGridServerSide } from "../../../../../../hooks/useMuiGridServerSide";
import useUserData from "../../../../../../hooks/user/useUserData";
import { EXHIBITION, EXHIBITOR_PROFILE } from "../../../../../../locales/components/namespaces";
import UpdateVisitorDialogue from "./updateVisitorDialogue";

type Props = {};

export const GridToolbar = ({ exportData, t, loading }) => {
	return (
		<CustomToolbar
			showDensity={false}
			showExport={false}
		>
			<LoadingButton
				onClick={exportData}
				loading={loading}
				variant={"text"}
				size={"small"}
			>
				{t('Export')}
			</LoadingButton>
		</CustomToolbar>
	)
}

const ScannedVisitors = (props: Props) => {
	const { exhibitor, exhibitionId: id } = props
	const { exhibition } = useExhibition({ noFetchFiles: true })
	const { t } = useTranslation(EXHIBITOR_PROFILE)
	const { language, token } = useUserData()
	const apiRef = useGridApiRef();
	const [loadingExport, setLoadingExport] = useState(false)
	const [loadingDelete, setLoadingDelete] = useState(false)
	const [confirmDeleteVisitor, setConfirmDeleteVisitor] = useState({
		open: false,
		loading: false,
		id: ''
	})
	const [formOpen, setFormOpen] = useState({
		open: false,
		data: null,
		comments: ''
	})
	const {
		isLoading,
		dataManager,
		data,
		total,
		page,
		pageSize,
		onPaginationModelChange,
		onSortModelChange,
		onFilterModelChange,
		convertFilterModel,
		convertSortModel
	} = useMuiGridServerSide({
		url: `exhibition/${id}/checkvisitor/${exhibitor.id}/index`,
		initialPageSize: 50,
		initialSortModel: [{ name: 'id', direction: 'descending' }],
	})
	const exportData = useCallback(() => {
		const sortModel = gridSortModelSelector(apiRef)
		const where = gridFilterModelSelector(apiRef)
		const columns = gridVisibleColumnDefinitionsSelector(apiRef)
		const data = {
			sorted: convertSortModel(sortModel),
			where: convertFilterModel(where, null),
			columns: columns.filter(f => f.type !== 'custom' && f.type !== 'actions').map(i => ({
				id: i.field,
				title: i.headerName
			}))
		}
		setLoadingExport(true)
		axios({
			url: `${API}/${language}/exhibition/${id}/checkvisitor/${exhibitor.id}/export`,
			method: 'post',
			responseType: "blob",
			data: data,
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
		}).then((response) => {
			const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
			const href = window.URL.createObjectURL(blob)
			downloadFile(href, 'export-visitors')
			setLoadingExport(false)
		}).catch((error) => {
			setLoadingExport(false)
			enqueueSnackbar(handleAxiosErrors(error, t('Failed to export!')), {
				variant: 'error',
				autoHideDuration: null,
			})
		})
	}, [apiRef, convertFilterModel, convertSortModel, exhibitor.id, id, language, t, token])
	
	const deleteVisitorEntry = useCallback((id) => {
		setLoadingDelete(true)
		axios({
			url: `${API}/${language}/exhibition/${id}/checkvisitor/delete/${id}`,
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
		}).then((response) => {
			enqueueSnackbar(t('Entry removed successfully!'), {
				variant: 'success'
			})
			setConfirmDeleteVisitor(old => ({
				open: false,
				loading: false,
				id: ''
			}))
			dataManager()
			setLoadingDelete(false)
		}).catch((error) => {
			setLoadingDelete(false)
			enqueueSnackbar(handleAxiosErrors(error, t('Failed to delete entry!')), {
				variant: 'error',
				autoHideDuration: null,
			})
			dataManager()
		})
	}, [dataManager, language, t, token])
	
	const columns = useMemo(() => [
		{
			headerName: t('Actions'),
			field: 'actions',
			type: 'actions',
			getActions: (params) => [
				<GridActionsCellItem
					onClick={() => setFormOpen({
						open: true,
						data: params.row,
						comments: params.row.Comments
					})}
					label={t("Edit")}
					icon={<EditIcon/>}
					color="primary"
				/>,
				<GridActionsCellItem
					
					onClick={() => setConfirmDeleteVisitor({
						open: true,
						loading: false,
						id: params.id
					})}
					icon={<TrashIcon/>}
					label={t("Remove")}
					color={"error"}
				/>,
			],
		},
		{
			field: 'Name',
			headerName: t('Name'),
			description: t('Name'),
			minWidth: 300,
			flex: 1,
			renderCell: ({ value }) => {
				return <TruncateDescription value={value}/>
			},
		},
		{
			field: 'CompanyName',
			headerName: t('Company Name'),
			description: t('Company Name'),
			minWidth: 300,
			flex: 1,
			renderCell: ({ value }) => {
				return <TruncateDescription value={value}/>
			},
		},
		{
			field: 'VatRegNo',
			headerName: t('VatRegNo'),
			description: t('VatRegNo'),
			minWidth: 180,
			flex: 1,
			renderCell: ({ value }) => {
				return <TruncateDescription value={value}/>
			},
		},
		{
			field: 'Position',
			headerName: t('Position'),
			description: t('Position'),
			minWidth: 140,
			flex: 1,
			renderCell: ({ value }) => {
				return <TruncateDescription value={value}/>
			},
		},
		{
			field: 'Email',
			headerName: t('Email'),
			description: t('Email'),
			minWidth: 240,
			flex: 1,
			renderCell: ({ value }) => {
				return <TruncateDescription value={value}/>
			},
		},
		{
			field: 'CompanyMobilePhone',
			headerName: t('CompanyMobilePhone'),
			description: t('CompanyMobilePhone'),
			minWidth: 150,
			flex: 1,
			renderCell: ({ value }) => {
				return <TruncateDescription value={value}/>
			},
		},
		{
			field: 'Country',
			headerName: t('Country'),
			minWidth: 160,
			flex: 1
		},
		{
			field: 'CompanyAddress',
			headerName: t('Address'),
			description: t('Address'),
			minWidth: 200,
			flex: 1,
			renderCell: ({ value }) => {
				return <TruncateDescription value={value}/>
			},
		},
		{
			field: 'CompanyCity',
			headerName: t('City'),
			description: t('City'),
			minWidth: 120,
			flex: 1,
			renderCell: ({ value }) => {
				return <TruncateDescription value={value}/>
			},
		},
		{
			field: 'CompanyPostalCode',
			headerName: t('CompanyPostalCode'),
			description: t('CompanyPostalCode'),
			minWidth: 120,
			flex: 1,
		},
		{
			field: 'CompanyBusinessPhone',
			headerName: t('CompanyBusinessPhone'),
			description: t('CompanyBusinessPhone'),
			minWidth: 150,
			flex: 1,
			renderCell: ({ value }) => {
				return <TruncateDescription value={value}/>
			},
		},
		
		{
			field: 'CompanyWebSite',
			headerName: t('CompanyWebSite'),
			description: t('CompanyWebSite'),
			minWidth: 200,
			flex: 1,
			renderCell: ({ value }) => {
				return <TruncateDescription value={value}/>
			},
		},
		{
			field: 'Facebook',
			headerName: t('Facebook'),
			description: t('Facebook'),
			minWidth: 200,
			flex: 1,
			renderCell: ({ value }) => {
				return <TruncateDescription value={value}/>
			},
		},
		{
			field: 'Instagram',
			headerName: t('Instagram'),
			description: t('Instagram'),
			minWidth: 200,
			flex: 1,
			renderCell: ({ value }) => {
				return <TruncateDescription value={value}/>
			},
		},
		{
			field: 'Comments',
			headerName: t('Comments'),
			description: t('Comments'),
			minWidth: 300,
			flex: 1,
			renderCell: ({ value }) => {
				return <TruncateDescription value={value}/>
			},
		},
	], [t])
	return (
		<Box>
			<Paper sx={{ mb: 1, p: 1 }} variant={"outlined"}>
				<CustomTranslation
					code={'my_visitors'}
					translations={exhibition?.translations}
				>
					<Trans
						i18nKey={'exhibitor_scanned_visitors'}
						ns={EXHIBITION}
					>
						Here you can scan your visitors. Access to exhibition is available for 7 days after exhibition
						end.
					</Trans>
				</CustomTranslation>
			</Paper>
			<Box>
				<DataGridWrapper>
					<DataGridPremium
						apiRef={apiRef}
						disableRowGrouping
						disableAggregation
						sx={{ bgcolor: 'background.paper' }}
						initialState={{ density: 'standard' }}
						columns={columns}
						loading={isLoading}
						rows={data}
						rowCount={Number(total)}
						localeText={muiGridLocales(language)}
						pageSizeOptions={[5, 10, 15, 20, 50, 100, 200]}
						pagination
						paginationMode='server'
						paginationModel={{ page, pageSize }}
						onPaginationModelChange={onPaginationModelChange}
						sortingMode="server"
						onSortModelChange={(sortModel) => onSortModelChange(sortModel)}
						filterMode='server'
						onFilterModelChange={(filterModel, details) => onFilterModelChange(filterModel, details)}
						slots={{
							toolbar: GridToolbar,
							pagination: CustomPagination
						}}
						slotProps={{
							toolbar: {
								exportData: exportData,
								t: t,
								loading: loadingExport
							}
						}}
					/>
				</DataGridWrapper>
			</Box>
			<ConfirmDialog
				loading={loadingDelete}
				withLoadingButton
				onYes={() => deleteVisitorEntry(confirmDeleteVisitor?.id)}
				onNo={() => {
					setConfirmDeleteVisitor({
						open: false,
						loading: false,
						id: ''
					})
				}}
				handleDialog={(newValue) => {
					setConfirmDeleteVisitor(old => ({
						...old,
						open: newValue,
					}))
				}}
				title={t('Confirm Delete')}
				open={confirmDeleteVisitor?.open}
				hasBtnIcons={false}
			>
				{t('This action is permanent. Press OK if you want to proceed.')}
			</ConfirmDialog>
			{formOpen?.open && (
				<UpdateVisitorDialogue
					formOpen={formOpen}
					selectedExhibition={id}
					setFormOpen={setFormOpen}
					onSuccess={dataManager}
				/>
			)}
		</Box>
	);
};

export default ScannedVisitors